<template>
  <div
    ref="coninput"
    class="vs-component vs-con-input-label vs-input"
    :class="[
      `vs-input-${color}`,
      $attrs.class,
      {
        isFocus,
        'input-icon-validate-success': success,
        'input-icon-validate-danger': danger,
        'input-icon-validate-warning': warning,
        'is-label-placeholder': labelPlaceholder,
      },
    ]"
    :style="[$attrs.style, styleLabel]"
  >
    <label
      v-if="!labelPlaceholder && label"
      class="vs-input--label"
      for=""
      @click="focusInput"
    >
      {{ label }}
    </label>

    <div class="vs-con-input">
      <input
        ref="vsinput"
        class="vs-inputx vs-input--input"
        :class="[
          size,
          {
            hasValue: currentValue !== '',
            hasIcon: icon,
            'icon-after-input': iconAfter,
          },
        ]"
        :style="inputStyle"
        :value="currentValue"
        :autofocus="autofocus"
        :placeholder="null"
        :type="inputType"
        v-bind="inputAttrs"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />

      <transition name="placeholderx">
        <span
          v-if="showPlaceholder"
          ref="spanplaceholder"
          class="input-span-placeholder vs-input--placeholder"
          :class="[
            labelPlaceholder && size,
            size,
            { 'vs-placeholder-label': labelPlaceholder },
          ]"
          :style="styleLabel"
          @click="focusInput"
        >
          {{ placeholderText }}
        </span>
      </transition>

      <vs-icon
        v-if="icon"
        class="icon-inputx notranslate vs-input--icon"
        :class="{
          'icon-after': iconAfter,
          'icon-no-border': iconNoBorder,
        }"
        :icon-pack="iconPack"
        :icon="icon"
        @click="handleIconClick"
      />

      <transition name="icon-validate">
        <span
          v-if="success || danger || warning"
          class="input-icon-validate vs-input--icon-validate"
          :class="{ 'icon-before': iconAfter }"
        >
          <vs-icon
            :class="{ 'icon-before': iconAfter }"
            :icon-pack="valIconPack"
            :icon="validationIcon"
          />
        </span>
      </transition>
    </div>

    <transition-group
      @before-enter="beforeEnter"
      @enter="enter"
      @leave="leave"
    >
      <div
        v-if="success"
        key="success"
        class="con-text-validation vs-input--text-validation"
      >
        <span
          class="span-text-validation span-text-validation-success vs-input--text-validation-span"
        >
          {{ successText }}
        </span>
      </div>
      <div
        v-else-if="danger"
        key="danger"
        class="con-text-validation span-text-validation-danger vs-input--text-validation-span"
      >
        <span class="span-text-validation">{{ dangerText }}</span>
      </div>
      <div
        v-else-if="warning"
        key="warning"
        class="con-text-validation span-text-validation-warning vs-input--text-validation-span"
      >
        <span class="span-text-validation">{{ warningText }}</span>
      </div>
      <div
        v-if="descriptionText"
        key="description"
        class="con-text-validation span-text-validation vs-input--text-validation-span"
      >
        <span class="span-text-validation">{{ descriptionText }}</span>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: "VsInput",
  inheritAttrs: false,
  props: {
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    value: {
      type: [String, Number],
      default: undefined,
    },
    labelPlaceholder: {
      type: [String, Number],
      default: null,
    },
    label: {
      type: [String, Number],
      default: null,
    },
    autofocus: {
      type: [Boolean, String],
      default: false,
    },
    icon: {
      type: String,
      default: null,
    },
    iconAfter: {
      type: [Boolean, String],
      default: false,
    },
    iconNoBorder: {
      type: Boolean,
      default: false,
    },
    iconPack: {
      type: String,
      default: "material-icons",
    },
    color: {
      type: String,
      default: "primary",
    },
    success: {
      type: Boolean,
      default: false,
    },
    danger: {
      type: Boolean,
      default: false,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    successText: {
      type: String,
      default: null,
    },
    dangerText: {
      type: String,
      default: null,
    },
    warningText: {
      type: String,
      default: null,
    },
    descriptionText: {
      type: String,
      default: null,
    },
    size: {
      type: String,
      default: "normal",
    },
    valIconPack: {
      type: String,
      default: "material-icons",
    },
    valIconSuccess: {
      type: String,
      default: null,
    },
    valIconDanger: {
      type: String,
      default: null,
    },
    valIconWarning: {
      type: String,
      default: null,
    },
  },
  emits: ["update:modelValue", "input", "focus", "blur", "icon-click"],
  data() {
    return {
      isFocus: false,
    };
  },
  computed: {
    currentValue() {
      if (this.modelValue !== undefined) {
        return this.modelValue;
      }
      return this.value === undefined ? "" : this.value;
    },
    inputType() {
      return this.$attrs.type || "text";
    },
    inputAttrs() {
      const attrs = { ...this.$attrs };
      [
        "class",
        "style",
        "type",
        "placeholder",
        "onInput",
        "onFocus",
        "onBlur",
      ].forEach((key) => delete attrs[key]);
      return attrs;
    },
    resolvedColor() {
      if (
        this.color.startsWith("#") ||
        this.color.startsWith("rgb") ||
        this.color.startsWith("hsl") ||
        this.color.startsWith("var(")
      ) {
        return this.color;
      }
      return `rgba(var(--vs-${this.color}), 1)`;
    },
    inputStyle() {
      return {
        border: `1px solid ${
          this.isFocus ? this.resolvedColor : "rgba(0, 0, 0,.2)"
        }`,
      };
    },
    styleLabel() {
      return {
        color: this.isFocus ? this.resolvedColor : null,
      };
    },
    showPlaceholder() {
      return Boolean(
        (this.labelPlaceholder || this.$attrs.placeholder) &&
          (this.labelPlaceholder || !this.currentValue)
      );
    },
    placeholderText() {
      return this.$attrs.placeholder || this.labelPlaceholder;
    },
    validationIcon() {
      if (this.danger) return this.valIconDanger;
      if (this.warning) return this.valIconWarning;
      if (this.success) return this.valIconSuccess;
      return "";
    },
  },
  methods: {
    handleInput(event) {
      this.$emit("update:modelValue", event.target.value);
      this.$emit("input", event.target.value);
    },
    handleFocus(event) {
      this.$emit("focus", event);
      this.isFocus = true;
    },
    handleBlur(event) {
      this.$emit("blur", event);
      this.isFocus = false;
    },
    handleIconClick() {
      this.focusInput();
      this.$emit("icon-click");
    },
    beforeEnter(element) {
      element.style.height = 0;
    },
    enter(element, done) {
      element.style.height = `${element.scrollHeight}px`;
      done();
    },
    leave(element) {
      element.style.height = "0px";
    },
    focusInput() {
      this.$refs.vsinput.focus();
    },
  },
};
</script>
